import React, { useCallback, useState } from 'react'

import { Button, Checkbox } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, objectOf, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import useCompanyForm from '../../Hooks/useCompanyForm'
import CompanyForm from '../CompanyForm/CompanyForm'

function CompanyDetails({
  locale,
  companyFormData,
  onApplied,
  onReverted,
  onToggleChange,
  savedCompanyData = null,
  isUpdateMode = false,
}) {
  const [isToggled, setIsToggled] = useState(!!savedCompanyData)

  const isDisabled = !!savedCompanyData?.isFromBusinessAccount

  const {
    control,
    errors,
    watchCountry,
    isApplied,
    isApplying,
    handleApply,
    handleFieldChange,
    handleRevert,
    resetForm,
    setValue,
  } = useCompanyForm({ onApplied, onReverted, savedCompanyData, isUpdateMode })

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const handleToggle = useCallback((checked) => {
    setIsToggled(checked)
    onToggleChange(checked)

    if (!checked) {
      resetForm()
      handleRevert()
    }
  }, [onToggleChange, handleRevert, resetForm])

  return (
    <div className="company-details">
      <Checkbox
        id="company-toggle"
        isChecked={isToggled}
        isControlled
        label={translate('subscription_checkout.company.toggle_label')}
        onChange={handleToggle}
        size="medium"
      />

      {isToggled && (
        <div className="company-details__content">
          <CompanyForm
            control={control}
            errors={errors}
            countries={companyFormData.countries}
            countriesWithStates={companyFormData.countriesWithStates}
            states={companyFormData.states}
            translate={translate}
            watchCountry={watchCountry}
            isDisabled={isDisabled}
            onFieldChange={handleFieldChange}
            setValue={setValue}
          />

          <Button
            type="primary"
            expand
            onClick={handleApply}
            disabled={isApplying || isApplied}
          >
            {isApplying
              ? translate(`subscription_checkout.company.${isUpdateMode ? 'updating' : 'applying'}`)
              : translate(`subscription_checkout.company.${isUpdateMode ? 'update_button' : 'apply_button'}`)}
          </Button>
        </div>
      )}
    </div>
  )
}

CompanyDetails.propTypes = {
  locale: string.isRequired,
  companyFormData: shape({
    countries: arrayOf(arrayOf(string)).isRequired,
    countriesWithStates: arrayOf(string).isRequired,
    states: objectOf(arrayOf(arrayOf(string))).isRequired,
  }).isRequired,
  onApplied: func.isRequired,
  onReverted: func.isRequired,
  onToggleChange: func.isRequired,
  isUpdateMode: bool,
  savedCompanyData: shape({
    companyName: string,
    companyCode: string,
    vatin: string,
    country: string,
    state: string,
    zipCode: string,
    city: string,
    streetAddress: string,
    isFromBusinessAccount: bool,
  }),
}

export default CompanyDetails
